import React, {useCallback, useState} from 'react'
import {useSelector} from 'react-redux'
import {
  CatalogWrapper,
  ProductsPanel,
  CatalogLoadingOverlay,
} from './ProductsCatalog.styles'
import {Spinner} from '../../../../../components/loader-spinner'
import ProductCatalogHeader from '../components/ProductsCatalogHeader'
import ProductCatalogProposalTab from '../components/ProductCatalogProposalTab'
import {CatalogGrid} from '../../../../../features/CatalogGrid'
import {RetainWebsiteDowngradeModal} from '../../../../../components/catalog-components/retain-website-downgrade-modal'
import {
  selectActiveOptionDisplayOrder,
  selectProductConfigInfo,
} from '../../../../../app-store/ProposalSlice'

import {useProductsCatalog} from '../hooks/useProductsCatalog'
import {useProposalSave} from '../hooks/useProposalSave'
import {useProposalPaymentPreview} from '../hooks/useProposalPaymentPreview'

export function ProductsCatalog({onPreview, onBack, isLocked}) {
  const activeOptionDisplayOrder = useSelector(selectActiveOptionDisplayOrder)
  const productConfigInfo = useSelector(selectProductConfigInfo)
  const {
    softwareGroups,
    addonGroups,
    alternatePlanCodesToHide,
    allPlansUnfiltered,
    isDataLoaded,
    selectedCountry,
    selectedProducts,
    selectedProductsByCode,
    disabledPlanCodes,
    validExtensionList,
    lockedBillingByGroup,
    showMCGroupAnnualToggle,
    isSocialContentGenGrandfathered,
    showGrowthPackageOnboarding,
    handleAddProduct,
    handleUpdateProduct,
    handleQuantityChange,
    pendingWebsiteDowngradePrompt,
    handleWebsiteDowngradeAnswer,
  } = useProductsCatalog()

  const {handleSave, saving: isSaveLoading} = useProposalSave()
  const {refreshPaymentPreview} = useProposalPaymentPreview()
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  const handlePreview = useCallback(async () => {
    setIsPreviewLoading(true)
    try {
      await refreshPaymentPreview()
      onPreview()
    } finally {
      setIsPreviewLoading(false)
    }
  }, [refreshPaymentPreview, onPreview])

  return (
    <CatalogWrapper>
      <ProductCatalogHeader
        onSave={handleSave}
        isSaveLoading={isSaveLoading}
        onPreview={handlePreview}
        isPreviewLoading={isPreviewLoading}
        onBack={onBack}
      />
      <ProductCatalogProposalTab />

      <ProductsPanel>
        {isLocked && (
          <CatalogLoadingOverlay>
            <Spinner />
          </CatalogLoadingOverlay>
        )}
        <CatalogGrid
          key={activeOptionDisplayOrder}
          softwareGroups={softwareGroups
            .map(group => ({
              ...group,
              plans: (group.plans ?? []).filter(
                plan => !alternatePlanCodesToHide.has(plan.code),
              ),
            }))
            .filter(group => group.plans.length > 0)}
          addonGroups={addonGroups}
          allPlans={allPlansUnfiltered}
          productConfigInfo={productConfigInfo}
          isDataLoaded={isDataLoaded}
          selectedCountry={selectedCountry}
          selectedProducts={selectedProducts}
          selectedProductsByCode={selectedProductsByCode}
          disabledPlanCodes={disabledPlanCodes}
          validExtensionList={validExtensionList}
          lockedBillingByGroup={lockedBillingByGroup}
          showMCGroupAnnualToggle={showMCGroupAnnualToggle}
          isSocialContentGenGrandfathered={isSocialContentGenGrandfathered}
          showGrowthPackageOnboarding={showGrowthPackageOnboarding}
          onAddProduct={handleAddProduct}
          onUpdateProduct={handleUpdateProduct}
          onQuantityChange={handleQuantityChange}
        />
      </ProductsPanel>

      <RetainWebsiteDowngradeModal
        isOpen={pendingWebsiteDowngradePrompt !== null}
        downgradedFrom={pendingWebsiteDowngradePrompt?.downgradedFromName}
        onConfirm={handleWebsiteDowngradeAnswer}
      />
    </CatalogWrapper>
  )
}
